import { useCallback } from 'react';

import { Button, ButtonProps } from '@/components/form/baseInputs/Button';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { useModalState } from '@/hooks/useModalState';
import {
  BehaviorAuthorizationType,
  useHasBehaviorAuthorization,
} from '@/modules/authentication/hooks/useHasBehaviorAuthorization';
import { HypotheticalILITModal } from '@/modules/entities/details/HypotheticalILITModal/HypotheticalILITModal';
import { HypotheticalTransferModal } from '@/modules/entities/details/HypotheticalTransferModal/HypotheticalTransferModal';
import { EstateWaterfallHypotheticalCharitableTrustModal } from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalCharitableTrustModal/EstateWaterfallHypotheticalCharitableTrustModal';
import { HypotheticalCharitableTrustModalKind } from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalCharitableTrustModal/EstateWaterfallHypotheticalCharitableTrustModal.types';
import { EstateWaterfallHypotheticalGratModal } from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalGratModal/EstateWaterfallHypotheticalGratModal';
import { EstateWaterfallHypotheticalSaleLoanModal } from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalSaleLoanModal/EstateWaterfallHypotheticalSaleLoanModal';
import { HypotheticalSaleLoanFormKind } from '@/modules/estateWaterfall/components/EstateWaterfallHypotheticalSaleLoanModal/EstateWaterfallHypotheticalSaleLoanModal.types';
import { UnreachableError } from '@/utils/errors';

import {
  CreateHypotheticalEventModal,
  HypotheticalEventType,
} from './CreateHypotheticalEventModal';

interface CreateHypotheticalEventButtonProps extends Partial<ButtonProps> {
  waterfallId: string;
  householdId: string;
  // we use this lifecycle hook to refetch potentially affected queries
  // when a submodal is closed
  onCloseSubmodal: () => void;
  initialSourceEntityId?: string;
}

export function CreateHypotheticalEventButton({
  waterfallId,
  householdId,
  onCloseSubmodal,
  initialSourceEntityId,
  ...buttonProps
}: CreateHypotheticalEventButtonProps) {
  const canModifyHypotheticalTransfers = useHasBehaviorAuthorization(
    BehaviorAuthorizationType.CAN_MODIFY_HYPOTHETICAL_TRANSFERS
  );
  const [
    { isModalOpen: isTransferModalOpen },
    { openModal: openTransferModal, closeModal: closeTransferModal },
  ] = useModalState<string>();
  const [
    { isModalOpen: isSaleLoanModalOpen, data: saleLoanKind },
    { openModal: openSaleLoanModal, closeModal: closeSaleLoanModal },
  ] = useModalState<HypotheticalSaleLoanFormKind>();
  const [
    { isModalOpen: isEventSelectionDialogOpen },
    {
      openModal: openEventSelectionDialog,
      closeModal: closeEventSelectionDialog,
    },
  ] = useModalState();
  const [
    { isModalOpen: isGratModalOpen },
    { openModal: openGratModal, closeModal: closeGratModal },
  ] = useModalState();
  const [
    { isModalOpen: isCharitableTrustModalOpen, data: charitableTrustModalKind },
    {
      openModal: openCharitableTrustModal,
      closeModal: closeCharitableTrustModal,
    },
  ] = useModalState<HypotheticalCharitableTrustModalKind>();
  const [
    { isModalOpen: isIlitModalOpen },
    { openModal: openIlitModal, closeModal: closeIlitModal },
  ] = useModalState();

  const handleCloseSaleLoanModal = useCallback(() => {
    closeSaleLoanModal();
    onCloseSubmodal?.();
  }, [closeSaleLoanModal, onCloseSubmodal]);

  const handleCloseTransferModal = useCallback(() => {
    closeTransferModal();
    onCloseSubmodal?.();
  }, [closeTransferModal, onCloseSubmodal]);

  const handleCloseGratModal = useCallback(() => {
    closeGratModal();
    onCloseSubmodal?.();
  }, [closeGratModal, onCloseSubmodal]);

  const handleCloseCharitableTrustModal = useCallback(() => {
    closeCharitableTrustModal();
    onCloseSubmodal?.();
  }, [closeCharitableTrustModal, onCloseSubmodal]);

  const handleCloseIlitModal = useCallback(() => {
    closeIlitModal();
    onCloseSubmodal?.();
  }, [closeIlitModal, onCloseSubmodal]);

  const handleEventSelection = (eventType: HypotheticalEventType | null) => {
    closeEventSelectionDialog();

    switch (eventType) {
      case null:
        // user just closed the dialog without selecting an option; do nothing
        break;
      case 'GIFT_TRANSFER':
        openTransferModal();
        break;
      case 'INSTALLMENT_SALE':
        openSaleLoanModal('sale');
        break;
      case 'INTRA_FAMILY_LOAN':
        openSaleLoanModal('loan');
        break;
      case 'HYPOTHETICAL_GRAT':
        openGratModal();
        break;
      case 'HYPOTHETICAL_CRT':
        openCharitableTrustModal(HypotheticalCharitableTrustModalKind.CRT);
        break;
      case 'HYPOTHETICAL_CLT':
        openCharitableTrustModal(HypotheticalCharitableTrustModalKind.CLT);
        break;
      case 'HYPOTHETICAL_ILIT':
        openIlitModal();
        break;
      default:
        throw new UnreachableError({
          case: eventType,
          message: `Unhandled hypothetical event type: ${eventType}`,
        });
    }
  };

  return (
    <>
      {isTransferModalOpen && (
        <HypotheticalTransferModal
          waterfallId={waterfallId}
          householdId={householdId}
          isOpen={isTransferModalOpen}
          onClose={handleCloseTransferModal}
          transferId={undefined}
        />
      )}
      {isSaleLoanModalOpen && saleLoanKind && (
        <EstateWaterfallHypotheticalSaleLoanModal
          waterfallId={waterfallId}
          householdId={householdId}
          isOpen={isSaleLoanModalOpen}
          onClose={handleCloseSaleLoanModal}
          saleLoanId={null}
          initialKind={saleLoanKind ?? 'sale'}
        />
      )}
      {isEventSelectionDialogOpen && (
        <CreateHypotheticalEventModal
          isOpen={isEventSelectionDialogOpen}
          onClose={handleEventSelection}
        />
      )}
      {isGratModalOpen && (
        <EstateWaterfallHypotheticalGratModal
          waterfallId={waterfallId}
          isOpen={isGratModalOpen}
          onClose={handleCloseGratModal}
          gratId={null}
          initialSourceId={initialSourceEntityId}
          householdId={householdId}
        />
      )}
      {isCharitableTrustModalOpen && charitableTrustModalKind && (
        <EstateWaterfallHypotheticalCharitableTrustModal
          waterfallId={waterfallId}
          isOpen={isCharitableTrustModalOpen}
          onClose={handleCloseCharitableTrustModal}
          initialSourceId={initialSourceEntityId}
          householdId={householdId}
          hcrtId={null}
          hcltId={null}
          charitableTrustModalKind={charitableTrustModalKind}
        />
      )}
      {isIlitModalOpen && (
        <HypotheticalILITModal
          waterfallId={waterfallId}
          onClose={handleCloseIlitModal}
          householdId={householdId}
          hilitId={null}
        />
      )}
      <Button
        size="sm"
        variant="primary"
        onClick={openEventSelectionDialog}
        startIcon={PlusIcon}
        {...buttonProps}
        disabled={buttonProps.disabled || !canModifyHypotheticalTransfers}
      >
        Create hypothetical transfer
      </Button>
    </>
  );
}
